import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import FlightTypeService from '../services/FlightTypeService';

interface UseFlightTypeDescriptionsReturn {
  flightTypes: string[];
  flightTypeOptions: { value: string; label: string; title: string }[];
  isLoading: boolean;
  error: string | null;
}

export const useFlightTypeDescriptions = (): UseFlightTypeDescriptionsReturn => {
  const auth = useAuth();
  const [flightTypes, setFlightTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlightTypes = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const token = auth.user?.access_token;
        const response = await FlightTypeService.getFlightTypeDescription(token);
        
        if (response.data && Array.isArray(response.data)) {
          const descriptions = response.data
            .map(item => item.flightTypeDescription)
            .filter(desc => desc);
          
          setFlightTypes(descriptions);
        } else {
          setError("Invalid flight type data format");
          setFlightTypes([]);
        }
      } catch (err) {
        setError("Error fetching flight types");
        setFlightTypes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlightTypes();
  }, [auth.user]);

  // Create flight type options from the API data with truncated labels if needed
  const flightTypeOptions = flightTypes.map(description => {
    const truncatedLabel = description.length > 50
      ? `${description.substring(0, 47)}...`
      : description;

    return {
      value: description,
      label: truncatedLabel,
      title: description // For tooltip on hover
    };
  });

  return { flightTypes, flightTypeOptions, isLoading, error };
};
